// TouchPOSPage.tsx
import { useState, useEffect, useRef, useCallback } from 'react'
import { ShoppingCart, User, Trash2, Clock, Package, AlertCircle, RefreshCw, Plus, Minus } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiClient } from '../lib/api'
import { useAuthStore } from '../store/authStore'

// Types based on your schema
interface Product {
  id: string
  code: string
  name: string
  description?: string
  category_id?: string
  price: number
  cost_price?: number
  barcode?: string
  image?: string
  is_active: boolean
  is_service: boolean
  track_inventory: boolean
  minimum_stock?: number
  allow_backorder: boolean
  button_color?: string
  is_featured: boolean
  allow_decimal_quantities: boolean
  stock_control_type?: string
  preparation_time_minutes?: number
  modifiers_allowed: boolean
  price_includes_tax: boolean
  is_returnable: boolean
  is_discountable: boolean
  created_at: string
  updated_at: string

  // Additional fields for display
  category_name?: string
  current_stock?: number
  reorder_level?: number
}

interface Category {
  id: string
  name: string
  parent_category_id?: string
  description?: string
  sort_order: number
  is_active: boolean
  color?: string
  image?: string
  created_at: string
  updated_at: string
}

interface CartItem {
  id: string
  product_id: string
  product_code: string
  product_name: string
  quantity: number
  unit_price: number
  original_price: number
  discount_percent: number
  discount_amount: number
  tax_percent: number
  tax_amount: number
  total_amount: number
  line_number: number
  preparation_instructions?: string
  course_number?: number
  seat_number?: number
  kitchen_status?: string
  round_number: number
  is_confirmed: boolean
  is_canceled: boolean
  is_voided: boolean
  special_instructions?: string
  cost_price?: number
  preparation_status?: string
  modifier_total: number
  added_at: string
  status: 'pending' | 'confirmed' | 'sent' | 'cancelled'
}

interface OrderData {
  order_number?: string
  order_date: string
  customer_id?: string
  user_id: string
  cash_register_id?: string
  business_day_id?: string
  table_id?: string
  room_id?: string
  status: string
  order_type: string
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  paid_amount: number
  change_amount: number
  payment_status: string
  notes?: string
  covers_count: number
  table_number?: string
  waiter_id?: string
  kitchen_status?: string
  preparation_time?: number
  round_number: number
  special_instructions?: string
  source_channel: string
  items: OrderItemData[]
}

interface OrderItemData {
  product_id: string
  product_code: string
  product_name: string
  line_number: number
  quantity: number
  unit_price: number
  original_price: number
  discount_percent: number
  discount_amount: number
  tax_percent: number
  tax_amount: number
  total_amount: number
  cost_price?: number
  preparation_instructions?: string
  course_number?: number
  seat_number?: number
  round_number: number
  special_instructions?: string
  kitchen_status?: string
}

interface SwipeState {
  startX: number
  currentX: number
  isDragging: boolean
}

export default function TouchPOSPage() {
  const [cart, setCart] = useState<CartItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [currentRound, setCurrentRound] = useState<number>(1)
  const [swipeState, setSwipeState] = useState<SwipeState>({ 
    startX: 0, 
    currentX: 0, 
    isDragging: false 
  })
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine)
  const [isPullRefreshing, setIsPullRefreshing] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentLineNumber, setCurrentLineNumber] = useState<number>(1)

  const { user } = useAuthStore()
  const categoryContainerRef = useRef<HTMLDivElement>(null)

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      toast.success('Connection restored')
      fetchProducts()
      fetchCategories()
    }

    const handleOffline = () => {
      setIsOnline(false)
      toast.error('Connection lost - Working offline')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true)
      try {
        await Promise.all([fetchProducts(), fetchCategories()])
      } catch (error) {
        console.error('Failed to load initial data:', error)
        toast.error('Failed to load initial data')
      } finally {
        setIsLoading(false)
      }
    }

    loadInitialData()
  }, [])

  // Pull-to-refresh handler
  const handlePullToRefresh = useCallback(async () => {
    if (!isOnline || isPullRefreshing) return

    setIsPullRefreshing(true)
    try {
      await Promise.all([fetchProducts(), fetchCategories()])
      toast.success('Data refreshed')
    } catch (error) {
      console.error('Refresh error:', error)
      toast.error('Failed to refresh data')
    } finally {
      setIsPullRefreshing(false)
    }
  }, [isOnline, isPullRefreshing])

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/api/inventory/products?limit=1000&is_active=true')
      const rawData = response.data.data || response.data

      if (Array.isArray(rawData)) {
        const formattedProducts = rawData.map((product: any) => ({
          id: product.id,
          code: product.code,
          name: product.name,
          description: product.description,
          category_id: product.category_id,
          price: Number(product.price || 0),
          cost_price: Number(product.cost_price || 0),
          barcode: product.barcode,
          image: product.image,
          is_active: Boolean(product.is_active),
          is_service: Boolean(product.is_service),
          track_inventory: Boolean(product.track_inventory),
          minimum_stock: Number(product.minimum_stock || 0),
          allow_backorder: Boolean(product.allow_backorder),
          button_color: product.button_color || getCategoryColor(product.category?.name || product.category_id || ''),
          is_featured: Boolean(product.is_featured),
          allow_decimal_quantities: Boolean(product.allow_decimal_quantities),
          stock_control_type: product.stock_control_type,
          preparation_time_minutes: Number(product.preparation_time_minutes || 0),
          modifiers_allowed: Boolean(product.modifiers_allowed),
          price_includes_tax: Boolean(product.price_includes_tax),
          is_returnable: Boolean(product.is_returnable),
          is_discountable: Boolean(product.is_discountable),
          created_at: product.created_at,
          updated_at: product.updated_at,
          category_name: product.category?.name || 'Uncategorized',
          current_stock: Number(product.stock?.quantity || 0),
          reorder_level: Number(product.reorder_level || 0)
        }))

        setProducts(formattedProducts)
      }
    } catch (error: any) {
      console.error('Failed to load products:', error)
      if (isOnline) {
        toast.error('Failed to load products. Please check your connection.')
      }
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/api/inventory/categories?is_active=true')
      const rawData = response.data.data || response.data

      if (Array.isArray(rawData)) {
        const allCategories = [{ 
          id: 'all', 
          name: 'All', 
          sort_order: 0, 
          is_active: true,
          color: 'bg-blue-600',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, ...rawData]

        setCategories(allCategories)
      }
    } catch (error: any) {
      console.error('Failed to load categories:', error)
      if (isOnline) {
        toast.error('Failed to load categories')
      }
    }
  }

  const getCategoryColor = (categoryName: string): string => {
    if (!categoryName || categoryName === 'all' || categoryName === 'All') return 'bg-blue-600'
    if (categoryName === 'Uncategorized') return 'bg-gray-500'
    const colors = [
      'bg-amber-600', 'bg-rose-600', 'bg-sky-600', 'bg-yellow-600',
      'bg-emerald-600', 'bg-violet-600', 'bg-orange-600', 'bg-teal-600',
      'bg-pink-600', 'bg-indigo-600', 'bg-lime-600', 'bg-cyan-600'
    ]
    let hash = 0
    for (let i = 0; i < categoryName.length; i++) {
      hash = ((hash << 5) - hash) + categoryName.charCodeAt(i)
      hash |= 0
    }
    return colors[Math.abs(hash) % colors.length]
  }

  // Swipe gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setSwipeState({
      startX: e.touches[0].clientX,
      currentX: e.touches[0].clientX,
      isDragging: true
    })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeState.isDragging) return
    setSwipeState(prev => ({
      ...prev,
      currentX: e.touches[0].clientX
    }))
  }

  const handleTouchEnd = () => {
    const deltaX = swipeState.currentX - swipeState.startX
    if (Math.abs(deltaX) > 100) {
      const currentIndex = categories.findIndex(cat => cat.id === selectedCategory)
      if (deltaX > 0 && currentIndex > 0) {
        // Swipe Right
        setSelectedCategory(categories[currentIndex - 1].id)
      } else if (deltaX < 0 && currentIndex < categories.length - 1) {
        // Swipe Left
        setSelectedCategory(categories[currentIndex + 1].id)
      }
    }
    setSwipeState(prev => ({ ...prev, isDragging: false }))
  }

  const addToCart = (product: Product) => {
    // Check if product is active
    if (!product.is_active) {
      toast.error(`${product.name} is not available`)
      return
    }

    // Check stock availability if inventory tracking is enabled
    const cartQuantity = cart
      .filter(item => 
        item.product_id === product.id && 
        !item.is_canceled && 
        !item.is_voided && 
        item.round_number === currentRound
      )
      .reduce((sum, item) => sum + item.quantity, 0)

    if (product.track_inventory && product.current_stock !== undefined) {
      const availableStock = product.current_stock
      const requestedQuantity = cartQuantity + 1

      if (requestedQuantity > availableStock && !product.allow_backorder) {
        toast.error(`Cannot add more. Only ${availableStock} in stock.`)
        return
      }

      if (availableStock <= (product.reorder_level || 5)) {
        toast.error(`Low stock: ${product.name} (${availableStock} remaining)`, {
          icon: '⚠️'
        })
      }
    }

    // Check if product already exists in cart for current round
    const existingItemIndex = cart.findIndex(item => 
      item.product_id === product.id && 
      !item.is_canceled && 
      !item.is_voided && 
      item.round_number === currentRound
    )

    if (existingItemIndex >= 0) {
      // Update existing item quantity
      const updatedCart = [...cart]
      const existingItem = updatedCart[existingItemIndex]
      const newQuantity = existingItem.quantity + 1

      // Double-check stock for increment
      if (product.track_inventory && product.current_stock !== undefined) {
        if (newQuantity > product.current_stock && !product.allow_backorder) {
          toast.error(`Cannot add more. Only ${product.current_stock} in stock.`)
          return
        }
      }

      const newTotalAmount = newQuantity * existingItem.unit_price

      updatedCart[existingItemIndex] = {
        ...existingItem,
        quantity: newQuantity,
        total_amount: newTotalAmount
      }

      setCart(updatedCart)
    } else {
      // Add new item to cart
      const newCartItem: CartItem = {
        id: `${product.id}-${Date.now()}`,
        product_id: product.id,
        product_code: product.code,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        original_price: product.price,
        discount_percent: 0,
        discount_amount: 0,
        tax_percent: 0,
        tax_amount: 0,
        total_amount: product.price,
        line_number: currentLineNumber,
        cost_price: product.cost_price,
        round_number: currentRound,
        is_confirmed: false,
        is_canceled: false,
        is_voided: false,
        kitchen_status: 'pending',
        preparation_status: 'pending',
        modifier_total: 0,
        added_at: new Date().toISOString(),
        status: 'pending'
      }

      setCart([...cart, newCartItem])
      setCurrentLineNumber(prev => prev + 1)
    }

    toast.success(`${product.name} added to cart`)
  }

  const updateCartItemQuantity = (itemId: string, change: number) => {
    const updatedCart = cart.map(item => {
      if (item.id === itemId) {
        const product = products.find(p => p.id === item.product_id)
        const newQuantity = item.quantity + change

        // Validate minimum quantity
        if (newQuantity < 1) {
          toast.error('Quantity cannot be less than 1')
          return item
        }

        // Check stock if inventory is tracked
        if (product?.track_inventory && product.current_stock !== undefined) {
          const cartQuantityForProduct = cart
            .filter(i => 
              i.product_id === product.id && 
              !i.is_canceled && 
              !i.is_voided && 
              i.id !== itemId
            )
            .reduce((sum, i) => sum + i.quantity, 0)

          const totalRequested = cartQuantityForProduct + newQuantity

          if (totalRequested > product.current_stock && !product.allow_backorder) {
            toast.error(`Only ${product.current_stock} in stock`)
            return item
          }
        }

        const newTotalAmount = newQuantity * item.unit_price

        return {
          ...item,
          quantity: newQuantity,
          total_amount: newTotalAmount
        }
      }
      return item
    })

    setCart(updatedCart)
  }

  const removeFromCart = (itemId: string) => {
    const item = cart.find(i => i.id === itemId)
    if (!item) return

    // Mark as canceled instead of removing for audit trail
    const updatedCart = cart.map(i => 
      i.id === itemId 
        ? { ...i, is_canceled: true, status: 'cancelled' as const }
        : i
    )

    setCart(updatedCart)
    toast.success(`${item.product_name} removed from cart`)
  }

  const confirmItem = (itemId: string) => {
    const updatedCart = cart.map(item =>
      item.id === itemId
        ? { ...item, is_confirmed: true, status: 'confirmed' as const }
        : item
    )
    setCart(updatedCart)
    toast.success('Item confirmed')
  }

  const sendToKitchen = (itemId: string) => {
    const updatedCart = cart.map(item =>
      item.id === itemId
        ? { ...item, kitchen_status: 'sent', status: 'sent' as const }
        : item
    )
    setCart(updatedCart)
    toast.success('Sent to kitchen')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 border-yellow-400 text-yellow-800'
      case 'confirmed': return 'bg-blue-100 border-blue-400 text-blue-800'
      case 'sent': return 'bg-green-100 border-green-400 text-green-800'
      case 'cancelled': return 'bg-red-100 border-red-400 text-red-800'
      default: return 'bg-gray-100 border-gray-400 text-gray-800'
    }
  }

  const getElapsedTime = (addedAt: string) => {
    const elapsed = Math.floor((Date.now() - new Date(addedAt).getTime()) / 60000)
    if (elapsed < 1) return 'Just now'
    if (elapsed < 60) return `${elapsed}m ago`
    const hours = Math.floor(elapsed / 60)
    return `${hours}h ago`
  }

  const calculateTotals = () => {
    const activeCartItems = cart.filter(item => !item.is_canceled && !item.is_voided)

    const subtotal = activeCartItems.reduce((sum, item) => sum + item.total_amount, 0)
    const discount = activeCartItems.reduce((sum, item) => sum + item.discount_amount, 0)
    const tax = activeCartItems.reduce((sum, item) => sum + item.tax_amount, 0)
    const total = subtotal - discount + tax

    return {
      subtotal: Number(subtotal.toFixed(2)),
      discount: Number(discount.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      total: Number(total.toFixed(2)),
      itemCount: activeCartItems.reduce((sum, item) => sum + item.quantity, 0)
    }
  }

  const handleCheckout = async () => {
    const activeCartItems = cart.filter(item => !item.is_canceled && !item.is_voided)

    if (activeCartItems.length === 0) {
      toast.error('Cart is empty')
      return
    }

    try {
      const totals = calculateTotals()
      const now = new Date().toISOString()

      const orderData: OrderData = {
        order_date: now,
        user_id: user?.id || '',
        status: 'open',
        order_type: 'sale',
        subtotal: totals.subtotal,
        discount_amount: totals.discount,
        tax_amount: totals.tax,
        total_amount: totals.total,
        paid_amount: 0,
        change_amount: 0,
        payment_status: 'unpaid',
        covers_count: 1,
        round_number: currentRound,
        source_channel: 'pos',
        items: activeCartItems.map((item, index) => ({
          product_id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          line_number: index + 1,
          quantity: item.quantity,
          unit_price: item.unit_price,
          original_price: item.original_price,
          discount_percent: item.discount_percent,
          discount_amount: item.discount_amount,
          tax_percent: item.tax_percent,
          tax_amount: item.tax_amount,
          total_amount: item.total_amount,
          cost_price: item.cost_price,
          preparation_instructions: item.preparation_instructions,
          course_number: item.course_number,
          seat_number: item.seat_number,
          round_number: item.round_number,
          special_instructions: item.special_instructions,
          kitchen_status: item.kitchen_status
        }))
      }

      const response = await apiClient.post('/api/touch-pos/direct-order', orderData)

      const orderResult = response.data?.data || response.data
      if (orderResult && (orderResult.id || orderResult.order_number)) {
        toast.success(`Order #${orderResult.order_number || 'created'} created successfully!`)
        // Reset cart for new order
        setCart([])
        setCurrentLineNumber(1)
        setCurrentRound(1)
      }
    } catch (error: any) {
      console.error('Checkout error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create order'
      toast.error(`Checkout failed: ${errorMessage}`)
    }
  }

  const clearCart = () => {
    if (cart.length === 0) {
      toast.error('Cart is already empty')
      return
    }

    // Mark all items as canceled
    const updatedCart = cart.map(item => ({
      ...item,
      is_canceled: true,
      status: 'cancelled' as const
    }))

    setCart(updatedCart)
    toast.success('Cart cleared')
  }

  const filteredProducts = selectedCategory === 'all' 
    ? products.filter(p => p.is_active)
    : products.filter(p => 
        p.is_active && 
        (p.category_id === selectedCategory || 
         categories.find(c => c.id === p.category_id)?.id === selectedCategory)
      )

  return (
    <div className="h-screen flex bg-gray-900 text-white overflow-hidden">
      {/* Left: Products */}
      <div className="flex-1 flex flex-col p-4">
        {/* Status Bar */}
        <div className="flex justify-between items-center mb-4 p-2 bg-gray-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm font-medium">
              {isOnline ? 'Online' : 'Offline'}
            </span>
            <span className="text-xs text-gray-400">
              User: {(user as any)?.first_name || 'Guest'}
            </span>
          </div>
          <div className="text-sm text-gray-400">
            {products.length} products loaded
          </div>
        </div>

        {/* Categories with Swipe Support */}
        <div 
          ref={categoryContainerRef}
          className="grid grid-cols-5 gap-2 mb-4"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`py-3 rounded-lg font-bold text-sm transition-all relative ${
                selectedCategory === cat.id
                  ? `${cat.color || 'bg-blue-600'} shadow-lg scale-[1.02]`
                  : 'bg-gray-800 hover:bg-gray-700'
              }`}
              title={cat.name}
            >
              <span className="truncate block px-1">{cat.name}</span>
              {selectedCategory === cat.id && (
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-1 bg-blue-400 rounded-full"></div>
              )}
            </button>
          ))}
        </div>

        {/* Round Selector */}
        <div className="flex gap-2 mb-4 items-center">
          <span className="text-sm font-semibold text-gray-400">Round:</span>
          {[1, 2, 3, 4].map(round => (
            <button
              key={round}
              onClick={() => setCurrentRound(round)}
              className={`px-4 py-2 rounded-lg font-bold transition-all ${
                currentRound === round
                  ? 'bg-purple-600 shadow-lg'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              R{round}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="flex-1 grid grid-cols-5 gap-2 overflow-auto auto-rows-min content-start">
          {filteredProducts.map(product => {
            const isOutOfStock = product.track_inventory && 
              (product.current_stock || 0) <= 0 && 
              !product.allow_backorder
            const isLowStock = product.track_inventory && 
              (product.current_stock || 0) <= (product.reorder_level || 5) && 
              (product.current_stock || 0) > 0

            return (
              <button
                key={product.id}
                onClick={() => addToCart(product)}
                className={`
                  ${product.button_color || getCategoryColor((product.category_name && product.category_name !== 'Uncategorized') ? product.category_name : (product.category_id || 'all'))} 
                  rounded-lg p-2 shadow-md hover:scale-[1.02] transition-transform 
                  flex flex-col items-center justify-center text-white relative h-24
                  ${isOutOfStock ? 'opacity-50 cursor-not-allowed grayscale' : ''}
                `}
                disabled={isOutOfStock}
                title={isOutOfStock ? 'Out of stock' : product.description}
              >
                {isLowStock && !isOutOfStock && (
                  <AlertCircle className="absolute top-1 left-1 text-yellow-300" size={16} />
                )}
                {product.is_service && (
                  <Package className="absolute top-1 right-1 text-white opacity-80" size={14} />
                )}
                <div className="text-sm font-bold mb-1 text-center line-clamp-2">
                  {product.name}
                </div>
                <div className="text-lg font-black">${product.price.toFixed(2)}</div>
                {product.track_inventory && product.current_stock !== undefined && (
                  <div className="text-[10px] mt-1 opacity-75 font-medium">
                    Stock: {product.current_stock}
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Right: Cart Panel */}
      <div className="w-96 bg-gray-800 flex flex-col border-l border-gray-700">
        {/* Cart Header */}
        <div className="p-4 border-b border-gray-700">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart size={28} />
            Order Cart
          </h2>
          <div className="text-sm text-gray-400 mt-1 flex items-center gap-2">
            <Clock size={14} />
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            <span className="ml-2 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
              Round {currentRound}
            </span>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {cart.filter(item => !item.is_canceled && !item.is_voided).length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
              <ShoppingCart size={64} className="mb-4 opacity-50" />
              <p className="text-xl font-medium">Cart is empty</p>
              <p className="text-sm mt-2 text-gray-400 text-center">
                Add products from the left panel
              </p>
            </div>
          ) : (
            cart
              .filter(item => !item.is_canceled && !item.is_voided)
              .map(item => {
                const product = products.find(p => p.id === item.product_id)
                return (
                  <div 
                    key={item.id} 
                    className={`rounded-xl p-3 border-2 ${getStatusColor(item.status)}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-bold">{item.product_name}</span>
                          <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                            R{item.round_number}
                          </span>
                        </div>
                        <div className="text-xs flex items-center gap-2">
                          <Clock size={12} />
                          {getElapsedTime(item.added_at)}
                          <span className="capitalize">• {item.status}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-600 hover:text-red-700 p-1"
                        title="Remove item"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateCartItemQuantity(item.id, -1)}
                          className="w-7 h-7 bg-gray-300 rounded-lg font-bold text-gray-700 hover:bg-gray-400 flex items-center justify-center"
                          disabled={item.quantity <= 1}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="w-10 text-center font-bold text-lg">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateCartItemQuantity(item.id, 1)}
                          className="w-7 h-7 bg-blue-500 rounded-lg font-bold text-white hover:bg-blue-600 flex items-center justify-center"
                          disabled={
                            product?.track_inventory && 
                            !product.allow_backorder && 
                            (product.current_stock || 0) < (item.quantity + 1)
                          }
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">
                          ${item.total_amount.toFixed(2)}
                        </div>
                        {item.discount_amount > 0 && (
                          <div className="text-xs text-green-600">
                            -${item.discount_amount.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Item Actions */}
                    <div className="mt-3 flex gap-2">
                      {item.status === 'pending' && (
                        <button
                          onClick={() => confirmItem(item.id)}
                          className="flex-1 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Confirm
                        </button>
                      )}
                      {item.status === 'confirmed' && (
                        <button
                          onClick={() => sendToKitchen(item.id)}
                          className="flex-1 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors"
                        >
                          Send to Kitchen
                        </button>
                      )}
                      {item.status === 'sent' && (
                        <span className="flex-1 py-1.5 text-center text-sm text-green-600">
                          Sent
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
          )}
        </div>

        {/* Cart Footer */}
        <div className="border-t border-gray-700 p-4 bg-gray-900">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-400">
              Items: {calculateTotals().itemCount}
            </div>
            <button 
              onClick={handlePullToRefresh}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              title="Refresh Products"
              disabled={isPullRefreshing}
            >
              <RefreshCw 
                size={18} 
                className={isPullRefreshing ? 'animate-spin' : ''} 
              />
            </button>
          </div>

          {/* Totals */}
          <div className="space-y-2 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-400">Subtotal:</span>
              <span className="text-lg">${calculateTotals().subtotal.toFixed(2)}</span>
            </div>
            {calculateTotals().discount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Discount:</span>
                <span className="text-lg text-green-500">
                  -${calculateTotals().discount.toFixed(2)}
                </span>
              </div>
            )}
            {calculateTotals().tax > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Tax:</span>
                <span className="text-lg">${calculateTotals().tax.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-gray-700">
              <span className="text-xl font-bold">Total:</span>
              <span className="text-3xl font-black text-blue-400">
                ${calculateTotals().total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleCheckout}
              disabled={cart.filter(item => !item.is_canceled && !item.is_voided).length === 0}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed py-4 rounded-xl text-xl font-bold shadow-lg transition-all hover:scale-[1.02] active:scale-95"
            >
              Complete Order
            </button>
            <button
              onClick={clearCart}
              disabled={cart.length === 0}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed py-3 rounded-xl text-lg font-bold transition-colors"
            >
              Clear Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}